import { useState, useCallback } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import { usePayablesRecords } from "./usePayablesRecords";
import { usePayablesMutations } from "./usePayablesMutations";

/**
 * usePayables
 * Contas a Pagar (LIABILITY).
 * Orquestrador S.O.L.I.D. conectando dados da tabela e service de mutação.
 */
export const usePayables = () => {
    const { idTenant, user: authUser } = useTenant();

    const [showForm, setShowForm] = useState(false);
    const [showAiModal, setShowAiModal] = useState(false);

    // 1. Hook de Leituras, Filtros e Balanço
    const {
        records, allRecords, summary, loading, error, hasMore, loadMore, refresh,
        statusFilter, setStatusFilter, search, setSearch, startDate, setStartDate, endDate, setEndDate, applyDateFilters
    } = usePayablesRecords(idTenant);

    // 2. Hook de Ações (Mutações no Banco)
    const {
        processing, createPayable, createPayablesBatch, markAsPaid, cancelRecord
    } = usePayablesMutations(idTenant, authUser, refresh);

    // Função encapsulada para fechar o form em caso de sucesso
    const handleCreatePayable = useCallback(async (formData) => {
        return createPayable(formData, () => setShowForm(false));
    }, [createPayable]);

    // Wrapper para import IA: após salvar, ajusta filtros de data para exibir os novos registros
    const handleCreatePayablesBatch = useCallback(async (resultsArray) => {
        const result = await createPayablesBatch(resultsArray);
        if (result !== false && resultsArray.length > 0) {
            // Calcular o range de datas das despesas importadas
            const dueDates = resultsArray.map(r => r.dueDate).filter(Boolean).sort();
            const minDate = dueDates[0];
            const maxDate = dueDates[dueDates.length - 1];

            // Expandir o filtro para cobrir o range importado + o dia atual
            const newStart = minDate < startDate ? minDate : startDate;
            const newEnd = maxDate > endDate ? maxDate : endDate;

            // Aplicar filtro imediatamente (com override direto)
            applyDateFilters(newStart, newEnd);
        }
        return result;
    }, [createPayablesBatch, startDate, endDate, applyDateFilters]);

    return {
        records,
        allRecords,
        summary,
        loading,
        error,
        hasMore,
        processing,
        showForm,
        showAiModal,
        statusFilter,
        search,
        startDate,
        endDate,
        setStatusFilter,
        setSearch,
        setStartDate,
        setEndDate,
        setShowForm,
        setShowAiModal,
        actions: {
            loadMore,
            refresh,
            markAsPaid,
            cancelRecord,
            createPayable: handleCreatePayable,
            createPayablesBatch: handleCreatePayablesBatch,
            applyDateFilters,
        },
    };
};
